import { API } from '@/services/api/main'
import request from '@/services/utils/request'
import {
    setVideoPlaybackTelemetryReporter,
    type VideoPlaybackTelemetryPayload
} from '@/packageLearn/utils/videoPlaybackTelemetry'
import { videoPlaybackFeatureConfig } from '@/packageLearn/config/videoPlayback'

let initialized = false

/**
 * 需要发送到远端的关键播放阶段。
 *
 * <p>远端接口首版主要用于定位 HLS 与 MP4 链路问题，因此保留切源、就绪、失败、
 * 会话起止等关键节点，避免把所有播放事件都写入服务端日志。</p>
 */
const REMOTE_TELEMETRY_STAGES = new Set([
    'session-start',
    'session-end',
    'loading',
    'skip-hls',
    'auto-downgrade',
    'fallback-mp4',
    'ready',
    'page-error',
    'play-failed',
    'manual-retry-m3u8',
    'manual-switch-hd'
])

/**
 * 判断当前遥测事件是否需要发送到远端。
 *
 * @param payload 当前播放遥测载荷
 * @returns `true` 表示需要上报到后端
 */
function shouldReportRemoteTelemetry(payload: VideoPlaybackTelemetryPayload) {
    return REMOTE_TELEMETRY_STAGES.has(payload.stage)
}

/**
 * 推断当前终端类型，便于服务端区分 H5 容器与设备差异。
 *
 * @returns 终端类型
 */
function resolveTerminalType() {
    try {
        const systemInfo = uni.getSystemInfoSync?.()
        return systemInfo?.platform || systemInfo?.osName || systemInfo?.deviceType || 'unknown'
    } catch (error) {
        console.warn('获取视频播放终端类型失败', error)
        return 'unknown'
    }
}

/**
 * 推断当前网络类型。
 *
 * @returns 网络类型
 */
function resolveNetworkType() {
    try {
        // #ifdef H5
        const connection = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection
        if (connection?.effectiveType) {
            return String(connection.effectiveType)
        }
        // #endif
        const systemInfo = uni.getSystemInfoSync?.()
        return systemInfo?.networkType || ''
    } catch (error) {
        console.warn('获取视频播放网络类型失败', error)
        return ''
    }
}

/**
 * 初始化视频播放远端遥测上报器。
 *
 * <p>全局仅初始化一次。成功后，播放器在保留本地诊断与 `uni.report` 的同时，
 * 会把关键播放阶段通过现有请求层发送到后端轻量上报接口。</p>
 */
export function initializeVideoPlaybackTelemetryReporter() {
    if (initialized) {
        return
    }
    initialized = true

    setVideoPlaybackTelemetryReporter(async (payload) => {
        if (!videoPlaybackFeatureConfig.enableRemoteTelemetryReporter || !shouldReportRemoteTelemetry(payload)) {
            return
        }
        await request({
            ...API.reportVideoPlaybackTelemetry,
            data: {
                ...payload,
                courseId: payload.courseId != null ? String(payload.courseId) : '',
                lessonId: payload.lessonId != null ? String(payload.lessonId) : '',
                networkType: resolveNetworkType(),
                terminalType: resolveTerminalType()
            }
        })
    })
}
